import _ from 'lodash';
import React, { Component } from 'react';
import classNames from 'classnames';
import { FormGroup, Col } from 'react-bootstrap';
import TagsInput from 'react-tagsinput';
import PropTypes from 'prop-types';

import { FormInputHelp, FormInputLabel, FormInputIcon, withFormContext } from 'blaze-form';

import { LIST_ITEM_DELIMITER } from '../../../util/form';
import '../../../styles/form/tags.scss';

const isInViewport = function (elem) {
  const bounding = elem.getBoundingClientRect();
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

class FormInputTagsItem extends Component {
  static getDerivedStateFromProps(newProps, prevState) {
    if (!_.isEqual(newProps.originalValue, prevState.originalValue)) {
      // This makes an assumption that save was hit, if this becomes an issue
      // the value can be checked again for true/false
      return {
        dirty: !_.isEqual(newProps.originalValue, newProps.value),
        originalValue: newProps.originalValue,
        tagLength: newProps.originalValue.length,
      };
    }

    // This was mainly to handle changes of validity on submit
    if (newProps.valid !== prevState.valid) {
      return {
        valid: newProps.valid,
        errorMessage: newProps.errorMessage,
      };
    }

    // TODO This causes problems in tables because it's not always getting the new props, so for
    // now we'll only set it to true
    if (newProps.touched === true && newProps.touched !== prevState.touched) {
      return {
        touched: newProps.touched,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    const { touched, errorMessage, originalValue, valid, value } = props;
    const dirty = !_.isEqual(value, originalValue);

    const initialValue = !Array.isArray(value) ? [] : value;

    this.state = {
      dirty,
      errorMessage,
      originalValue,
      touched,
      valid,
      value: initialValue,
      inputValue: '',

      // This will help track which tags are actually new and which are not
      tagLength: originalValue.length,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.addTag = this.addTag.bind(this);
    this.removeTag = this.removeTag.bind(this);
    this.renderTag = this.renderTag.bind(this);
    this.renderInput = this.renderInput.bind(this);

    this.tagsInput = React.createRef();
    this.controlIcon = React.createRef();
  }

  componentDidMount() {
    const { controlId, context } = this.props;
    context.ee.on(controlId, this.consume.bind(this));
  }

  componentWillUnmount() {
    const { controlId, context } = this.props;
    context.ee.off(controlId);
  }

  consume({ payload: data }) {
    // eslint-disable-line
    const { search } = this.props;

    if (data.options) {
      search.suggestions = data.options;
      this.forceUpdate();
    }
  }

  whichIcon() {
    const { search } = this.props;

    if (!search) {
      return null;
    }

    return 'search';
  }

  handleChange(value) {
    // console.debug('FormInputTagsItem handleChange value:', value);
    const { field } = this.props;

    this.props.onChange(field.name, value, () => {
      const result = this.props.onBlur(field.name);
      this.setState({ value: result.displayValue });
    });
    this.setState({ dirty: !_.isEqual(value, this.state.originalValue) });

    // This and shouldComponentUpdate may need to be used if this component is used in collections for performance reasons
    // In which case the return from this.props.onBlur will need to be used to update state!
    // this.forceUpdate();
  }

  handleBlur(suggestion) {
    // console.debug('FormInputTagsItem handleBlur value:', this.state.inputValue);
    const { search } = this.props;

    const useValue = search.optional && !suggestion ? this.state.inputValue : this.getSuggestionValue(suggestion);

    if (useValue !== '') {
      this.addTag(useValue);
    }
  }

  getSuggestionValue(suggestion) {
    if (!suggestion) {
      return '';
    }

    if (suggestion && typeof suggestion !== 'object') {
      return suggestion;
    }

    // When the suggestion is simple { label, value } (Mostly used in StaticSearch atm)
    if (suggestion.label && suggestion.value) {
      return `${suggestion.value}:${LIST_ITEM_DELIMITER}${suggestion.label}`;
    }

    // TODO unpredictable old stuff
    const suggestionKeys = Object.keys(suggestion);
    const itemid = suggestionKeys[0];
    const field = suggestionKeys[1];

    if (!field || !suggestion[field]) {
      return suggestion[itemid];
    }

    return `${suggestion[itemid]}:${LIST_ITEM_DELIMITER}${suggestion[field]}`;
  }

  addTag(value) {
    this.tagsInput.current.addTag(this.getSuggestionValue(value));
    this.setState({ inputValue: '' });
    this.scrollToBottom();
  }

  removeTag(key, callback) {
    let tagLength = this.state.tagLength;
    if (key + 1 <= tagLength) {
      tagLength -= 1;
    }

    this.setState({ tagLength });

    callback(key);
  }

  scrollToBottom() {
    if (this.tagsInput.current) {
      if (!isInViewport(this.tagsInput.current.div)) {
        setTimeout(() => {
          try {
            this.tagsInput.current.div.querySelector('.tags-bottom').scrollIntoView();
          } catch (e) {
            console.warn('Unable to scroll into view');
          }
        }, 200);
      }
    }
  }

  renderTag(props) {
    const { tag, key, onRemove, getTagDisplayValue, ...other } = props;

    let isValid = true;
    const isTagDirty = key >= this.state.tagLength;
    if (!this.state.valid && this.props.validIndex[key] === false) {
      isValid = false;
    }

    const chipClass = classNames('tags-tag', {
      'is-invalid': !isValid,
      'is-dirty': isTagDirty,
    });

    delete other.classNameRemove;
    delete other.className;

    const removeTag = () => {
      this.removeTag(key, onRemove);
    };

    const button = this.props.disabled ? (
      <i className="zmdi zmdi-lock zmdi-hc-lg remove-btn" onClick={removeTag} />
    ) : (
      <i className="zmdi zmdi-close-circle zmdi-hc-lg remove-btn" onClick={removeTag} />
    );

    return (
      <Col key={key} xs={12} sm={4} md={3} className="tag-col-wrapper">
        <div key={key} {...other}>
          <div className={chipClass}>
            <div className="chip-label" title={`${tag}`}>
              {getTagDisplayValue(tag) || '<Empty>'}
              {button}
            </div>
          </div>
        </div>
      </Col>
    );
  }

  renderInput(props) {
    const { controlId, search, inputComponent } = this.props;

    if (!search || !inputComponent) {
      const { onChange, onBlur, ...other } = props;
      delete other.addTag;

      return (
        <input type="text" onChange={onChange} onBlur={(e) => this.addTag(e.target.value)} value={this.state.inputValue} {...other} />
      );
    }

    const filtered = this.state.value.map(val => {
      if (!val || !val.split) {
        return val;
      }
      return val.split(`:${LIST_ITEM_DELIMITER}`)[0];
    });

    const filterClosure = (idField = 'id') => {
      return suggestions => {
        return _.filter(suggestions, row => {
          // StaticSearch uses 'value' other structs use 'id'
          const id = row[idField] || row.id;

          // if id in filtered array (currently selected values), remove from suggestions
          const filteredOut = !filtered.includes(String(id));

          if (filteredOut && search.filter && search.filter.includes) {
            // filter is defined, id must be present to be included in suggestions
            return search.filter.includes(id);
          }

          return filteredOut;
        });
      };
    };

    const InputComponent = this.props.inputComponent;
    const { filterBy, ...rest } = this.props.inputProps;

    // TODO search props should be in layout if they are different from the defaults
    const inputProps = {
      clearOnSelect: true,
      controlId,
      highlightFirstSuggestion: !search.optional,
      onBlur: this.handleBlur,
      onChange: v => this.setState({ inputValue: v }),
      onSuggestionSelected: this.addTag,
      showAll: search.showAll || false,
      sortBy: search.sortBy || '',
      allowSelectAll: search.allowSelectAll || false,
      value: this.state.inputValue,
      ...rest,
    };

    if (filterBy && inputProps.filterSuggestions) {
      const filterSuggestions = inputProps.filterSuggestions;
      inputProps.filterSuggestions = suggestions => filterSuggestions(filterClosure(filterBy)(suggestions));
    } else if (filterBy) {
      inputProps.filterSuggestions = filterClosure(filterBy);
    }

    if (search.command) {
      inputProps.command = search.command;
    }

    if (search.page) {
      inputProps.pageName = search.page;
    }

    if (search.modelName) {
      inputProps.modelName = search.modelName;
    }

    if (search.keyValue) {
      inputProps.validationKeyValue = search.keyValue;
    }

    if (search.filter) {
      inputProps.filter = search.filter;
    }

    if (search.suggestions) {
      inputProps.suggestions = search.suggestions;
    }

    if (search.local) {
      inputProps.localSearch = search.local;
    }

    return <InputComponent {...inputProps} />;
  }

  renderLayout(tagComponents, inputComponent) {
    return (
      <span>
        <div className="tags-container">
          {tagComponents}
          <div className="tags-bottom" />
        </div>
        {inputComponent}
      </span>
    );
  }

  render() {
    const { controlId, field, disabled, noLabel, context } = this.props;
    const additionalProps = {};
    const icon = this.whichIcon();

    // Invalid/Dirty as far as when the UI should show it
    const invalid = !this.state.valid && this.state.touched;
    const dirty = this.state.dirty && this.state.valid && !field.readOnly && context.trackDirty;

    const containerClass = classNames('form-tags', {
      'has-icon': !!icon,
      'is-enabled': !field.readOnly && !disabled,
      'is-invalid': invalid,
      'is-dirty': dirty,
      'is-disabled': disabled,
      'is-readonly': field.readOnly,
      [field.type]: !!field.type && field.type !== 'string',
    });

    const placeholder = disabled || field.readOnly ? 'Disabled' : 'Enter value';

    return (
      <FormGroup
        controlId={controlId}
        className={containerClass}
        key={controlId}
        id={`${controlId}-control`}
        {...additionalProps}
      >
        <FormInputLabel hide={noLabel}>{field.label}</FormInputLabel>
        <TagsInput
          className="tags-wrapper"
          focusedClassName="tags-wrapper--focused"
          inputProps={{
            placeholder,
            id: controlId,
            disabled,
            className: 'tags-input form-control',
          }}
          tagProps={{
            className: 'tags-tag',
            classNameRemove: 'tags-tag-remove',
          }}
          onChange={this.handleChange}
          onlyUnique={this.props.onlyUnique}
          ref={this.tagsInput}
          renderInput={this.renderInput}
          renderLayout={this.renderLayout}
          renderTag={this.renderTag}
          value={this.state.value}
        />
        <FormInputIcon icon={icon} iconRef={this.controlIcon} />
        <div className="tags-count-wrapper">
          <div className="tags-count">{this.state.value.length}</div>
        </div>
        <FormInputHelp errorText={this.state.errorMessage}>{field.helpText}</FormInputHelp>
      </FormGroup>
    );
  }
}

FormInputTagsItem.defaultProps = {
  errorMessage: '',
  field: {},
  onBlur: () => {},
  onChange: () => {},
  inputProps: {},
  noLabel: false,
  onlyUnique: true,
  originalValue: [],
  touched: false,
  valid: true,
  validIndex: [],
  value: [],
};

FormInputTagsItem.propTypes = {
  context: PropTypes.object,
  controlId: PropTypes.string,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  field: PropTypes.object,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  inputComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  inputProps: PropTypes.object,
  noLabel: PropTypes.bool,
  onlyUnique: PropTypes.bool,
  originalValue: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  search: PropTypes.object,
  touched: PropTypes.bool,
  valid: PropTypes.bool,
  validIndex: PropTypes.array,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default withFormContext(FormInputTagsItem);
